import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { recordPageVisit } from '../services/permissions';
import { canRead, hasSiteEntry, hasGrant, SENSITIVE_SECTIONS, SITES } from '../config/sitePermissions';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
  Button,
  ButtonBase,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  Divider,
  Chip,
  Avatar,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  Draw as DrawIcon,
  Checklist as ChecklistIcon,
  //LocalShipping as ShippingIcon,
  Calculate as CalculatorIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  MailOutline as MailOutlineIcon,
  Visibility as PreviewIcon,
  ConfirmationNumber as TicketIcon,
  TrendingUp as SalesIcon,
  AccountBalance as AccountingIcon,
  Api as ApiIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { logout, getCurrentUser } from '../services/auth';
import { User, UserRole } from '../types/user';
import cachedApiService from '../services/apiWithCache';
import { TASKS_FONT } from '../theme/tasksTheme';

// The white top bar's ink: the logo's navy, and a quieter grey beside it.
const BAR_TEXT = '#0f1e45';
const BAR_MUTED = '#64748b';
const BAR_HOVER = '#f4f6fb';

const drawerWidth = 256;

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  roles: UserRole[]; // Which roles can access this item
  description?: string;
  /** Section key in sitePermissions.portal that gates this item. */
  section: string;
  /** Rarely used now: kept, but folded away under "More tools" at the foot of the menu. */
  tucked?: boolean;
}

const menuItems: MenuItem[] = [
  // There is only one Dashboard now: what was Dashboard V2 sits at /admin. The
  // original one and the Updates page are hidden on purpose -- this covers both --
  // and their routes still work for anyone with a direct link.
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin', roles: ['admin', 'worker'] , section: 'dashboard-v2'},
  // { text: 'Updates', icon: <UpdateIcon />, path: '/admin/updates', roles: ['admin', 'worker'], description: 'Manage shipment updates' , section: 'updates'},
  { text: 'Tasks', icon: <ChecklistIcon />, path: '/admin/tasks', roles: ['admin', 'worker'], description: 'What is due, for whom, this week', section: 'tasks'},
  
  // Admin-only items
  // An admin sets people up inside Permissions, so Users is hidden from them. Staff
  // handed client and dealer account setup have no Permissions tab, so this is the
  // only way in for them -- shown once the Users section has been ticked, which
  // happens by itself when that switch is turned on.
  { text: 'Clients & Dealers', icon: <PeopleIcon />, path: '/admin/users', roles: ['worker'], description: 'Set up client and dealer logins', section: 'users'},
  // Hidden on purpose: sending an email about a shipment now lives in Dashboard V2's
  // Communication tab. The page still answers at /admin/email for the whole-system
  // email history.
  // { text: 'Email Notifications', icon: <EmailIcon />, path: '/admin/email', roles: ['admin', 'worker'] , section: 'email-notifications'},
  { text: 'Email Builder', icon: <MailOutlineIcon />, path: '/admin/email-builder', roles: ['admin', 'worker'] , section: 'email-builder', tucked: true},
  { text: 'Email Preview', icon: <PreviewIcon />, path: '/admin/email-preview', roles: ['admin', 'worker'] , section: 'email-preview', tucked: true},
  // Ports moved to the pricing API's admin panel (limaapi.onrender.com/admin), next to the fees they drive.
  { text: 'Calculator', icon: <CalculatorIcon />, path: '/admin/calculator', roles: ['admin', 'worker'] , section: 'calculator', tucked: true},
  { text: 'Ticket Hub', icon: <TicketIcon />, path: '/admin/ticket-hub', roles: ['admin', 'worker'] , section: 'ticket-hub'},
  // Setup rather than day-to-day work, so it sits at the bottom of the list.
  { text: 'Permissions', icon: <SecurityIcon />, path: '/admin/permissions', roles: ['admin'], description: 'Who can access what' , section: 'permissions'},
  { text: 'LIMAsign', icon: <DrawIcon />, path: '/admin/limasign', roles: ['admin', 'worker'], description: 'Send documents for e-signature', section: 'limasign'},
];

// The other LIMA sites, opened in a new tab. Each shows only to people who may sign in
// there: admins, and staff with that site ticked on the Permissions page.
const OTHER_SITES: { text: string; site: string; path: string; icon: React.ReactElement }[] = [
  { text: 'Sales', site: 'leads', path: '/login', icon: <SalesIcon /> },
  { text: 'Accounting', site: 'accounting', path: '/login', icon: <AccountingIcon /> },
  { text: 'API', site: 'api', path: '/admin/login', icon: <ApiIcon /> },
];
const siteUrl = (key: string) => (SITES.find((x) => x.key === key)?.url || '').replace(/\/$/, '');
const mayUseSite = (user: any, key: string) =>
  user?.role === 'admin'
  || Object.values((user?.sitePermissions || {})[key] || {}).some((level) => level && level !== 'none');

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAt, setUserMenuAt] = useState<HTMLElement | null>(null);
  // How many Ticket Hub tickets are still open, for the badge on that menu item.
  const [openTickets, setOpenTickets] = useState(0);
  const [user, setUser] = useState<User | null>(null);
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  // Tell the server which page is open, so Permissions can show each person's last
  // whereabouts. Fire and forget -- a failure here must never affect the page.
  useEffect(() => {
    recordPageVisit(location.pathname);
  }, [location.pathname]);



  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  // Keep the Ticket Hub badge current: on load, when the page changes, and every
  // couple of minutes. Silent -- a hiccup here just leaves the badge as it was.
  useEffect(() => {
    let alive = true;
    const loadCount = async () => {
      try {
        const token = localStorage.getItem('lima_auth_token');
        if (!token) return;
        const res = await fetch('/.netlify/functions/ticket-hub?what=count', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setOpenTickets(Number(data.open) || 0);
      } catch (_) {
        /* leave the badge alone */
      }
    };
    loadCount();
    const timer = setInterval(loadCount, 120000);
    return () => { alive = false; clearInterval(timer); };
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate to login even if the API call fails
      navigate('/login');
      window.location.reload();
    }
  };

  const handleClearData = async () => {
    await cachedApiService.clearAllData();
    window.location.reload();
  };

  // Filter menu items based on user role
  // Role decides the baseline; the Permissions page can then narrow it per person.
  //
  // Someone never set up on the Permissions page keeps the role-based access they
  // have always had, so turning this on does not silently lock out existing staff.
  // Once their access has been set -- including setting every section to none,
  // which is a deliberate "no access" -- what was set governs what they see.
  const configured = hasSiteEntry(user as any, 'portal');

  const allowedMenuItems = menuItems.filter((item) => {
    if (!user?.role) return false;
    // Permissions and Ports hand over the keys, so they are shown only
    // when the section has actually been ticked for this person -- never by default.
    if (SENSITIVE_SECTIONS.includes(item.section)) {
      return hasGrant(user as any, 'portal', item.section);
    }
    if (!item.roles.includes(user.role)) return false;
    if (user.role === 'admin' || !configured) return true;
    return canRead(user, 'portal', item.section);
  });


  // Exactly one item is highlighted: the longest menu path the URL matches.
  //
  // Dashboard sits at /admin, the parent of every admin page, so a plain prefix test
  // lit it up everywhere -- Dashboard and Dashboard V2 were both highlighted at once.
  // Taking the longest match instead means /admin only wins when nothing deeper does,
  // while a sub-page such as the standalone shipment view still highlights its parent.
  const activePath = allowedMenuItems
    .filter((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path ?? '';

  const isActive = (path: string) => path === activePath;

  const otherSites = OTHER_SITES.filter((x) => mayUseSite(user, x.site) && siteUrl(x.site));

  // The everyday pages, then the rarely used ones that sit folded at the foot of the menu.
  const mainItems = allowedMenuItems.filter((item) => !item.tucked);
  const tuckedItems = allowedMenuItems.filter((item) => item.tucked);
  // Folded by default. Opening it is remembered, and it is always open while one of
  // its pages is the page being looked at.
  const [moreChosen, setMoreChosen] = useState(() => { try { return localStorage.getItem('lima_menu_more') === '1'; } catch { return false; } });
  const moreOpen = moreChosen || tuckedItems.some((item) => item.path === activePath);
  const toggleMore = () => { const next = !moreOpen; setMoreChosen(next); try { localStorage.setItem('lima_menu_more', next ? '1' : '0'); } catch { /* not remembered */ } };

  // Determine interface type based on user role
  const isWorker = user?.role === 'worker';
  const interfaceTitle = isWorker ? 'LIMA Team' : 'LIMA Admin';
  const interfaceSubtitle = isWorker ? 'Team Portal' : 'Admin Portal';
  // "WORKER" is the value the account carries, not a word to put in front of someone.
  // The team reads "TEAM"; an admin still reads "ADMIN".
  const roleLabel = isWorker ? 'TEAM' : user?.role?.toUpperCase();
  // Use headerBgColor for both background and text color styling
  const headerBgColor = isWorker ? 'success.main' : 'primary.main';

  // The top bar names the page you are on.
  const pageTitle = allowedMenuItems.find((item) => isActive(item.path))?.text || interfaceTitle;
  const userName = user?.clientName || user?.uname || '';
  const userInitial = (userName.trim()[0] || '?').toUpperCase();
  const roleText = (isWorker ? 'Team' : user?.role === 'admin' ? 'Admin' : user?.role || '') + (user?.isPremiumWorker ? ' · Premium' : '');

  const renderItem = (item: MenuItem) => (
          <ListItemButton
            key={item.text}
            selected={isActive(item.path)}
            onClick={() => {
              navigate(item.path);
              if (isMobile) {
                handleDrawerToggle();
              }
            }}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              position: 'relative',
              '&:hover': {
                bgcolor: isWorker ? 'success.50' : 'primary.50',
              },
              '&.Mui-selected': {
                bgcolor: isWorker ? 'success.50' : 'primary.50',
                '&:hover': { bgcolor: isWorker ? 'success.100' : 'primary.100' },
                // A bar down the left edge so the active page is obvious at a glance,
                // not just a faint tint.
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 6,
                  bottom: 6,
                  width: 3,
                  borderRadius: 3,
                  bgcolor: headerBgColor,
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: headerBgColor, minWidth: 40 }}>
              {item.section === 'ticket-hub' && openTickets > 0 ? (
                <Badge
                  badgeContent={openTickets}
                  max={99}
                  color="error"
                  overlap="circular"
                  sx={{ '& .MuiBadge-badge': { fontWeight: 700, fontSize: '0.65rem', minWidth: 18, height: 18 } }}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: isActive(item.path) ? 500 : 400,
                color: isActive(item.path) ? headerBgColor : undefined,
              }}
            />
          </ListItemButton>
  );

  const drawer = (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      // The menu wears the Tasks type: Inter, 14px.
      '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiBadge-badge': { fontFamily: TASKS_FONT },
      '& .MuiButton-root': { fontSize: 14, fontWeight: 500 },
    }}>
      {/* The logo and which portal this is, on white, level with the top bar.
          Same sizes as the Sales Hub's brand: 34px logo, 14px name, 14px apart. */}
      <Toolbar sx={{ bgcolor: '#fff', gap: '14px', pl: { xs: '28px', sm: '28px' }, pr: { xs: '12px', sm: '12px' } }}>
        {/* The logo always goes back to the Dashboard. */}
        <ButtonBase onClick={() => { navigate('/admin'); if (isMobile) handleDrawerToggle(); }} aria-label="Go to the Dashboard" title="Dashboard" sx={{ borderRadius: 1 }}>
          <Box component="img" src="/lima-logo-dark.webp" alt="LIMA Automotive Industries" sx={{ height: 34, width: 'auto', display: 'block' }} />
        </ButtonBase>
        <Typography noWrap sx={{ color: BAR_MUTED, fontSize: 14, fontWeight: 400 }}>
          {interfaceSubtitle}
        </Typography>
      </Toolbar>
      
      <Divider />
      
      {/* User Info */}
      {user && (
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: headerBgColor }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {user.clientName || user.uname}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={roleLabel}
                  size="small"
                  color={isWorker ? 'success' : 'primary'}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
                {user.isPremiumWorker && (
                  <Chip
                    label="PREMIUM"
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      
      <Divider />
      
      {/* The everyday pages, packed at the top. The list takes the room that is left and
          scrolls inside itself if it ever runs out, so nothing below can push it about. */}
      <List sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pt: 2 }}>
        {mainItems.map(renderItem)}

        {otherSites.length > 0 && (
          <>
            <Typography sx={{ px: 3, pt: 2, pb: 1, fontSize: 14, fontWeight: 600, color: '#1b2236' }}>
              Other Sites
            </Typography>
            {otherSites.map((site) => (
              <ListItemButton
                key={site.text}
                component="a"
                href={`${siteUrl(site.site)}${site.path}`}
                target="_blank"
                rel="noopener"
                onClick={() => { if (isMobile) handleDrawerToggle(); }}
                sx={{
                  mx: 1, borderRadius: 1, mb: 0.5,
                  '&:hover': { bgcolor: isWorker ? 'success.50' : 'primary.50' },
                  '&:active': { bgcolor: isWorker ? 'success.100' : 'primary.100' },
                }}
              >
                <ListItemIcon sx={{ color: headerBgColor, minWidth: 40 }}>{site.icon}</ListItemIcon>
                <ListItemText primary={site.text} primaryTypographyProps={{ fontSize: 14, fontWeight: 400 }} />
                <OpenInNewIcon sx={{ fontSize: 16, color: '#6f7890' }} />
              </ListItemButton>
            ))}
          </>
        )}
      </List>

      {/* Pages that are rarely used now. Not removed: folded under one line at the foot
          of the menu, the way Quick links are on the Ticket Hub. They open upwards from
          that line, over the empty part of the menu, and move nothing above them. */}
      {tuckedItems.length > 0 && (
        <Box sx={{ flex: '0 0 auto', pt: 1, pb: 1 }}>
          <Collapse in={moreOpen} unmountOnExit>
            <List disablePadding>{tuckedItems.map(renderItem)}</List>
          </Collapse>
          <ButtonBase
            onClick={toggleMore}
            aria-expanded={moreOpen}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              width: 'calc(100% - 16px)', mx: 1, height: 36, borderRadius: '11px',
              border: '2px solid transparent', color: '#6f7890',
              fontSize: 12, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase',
              '&:hover': { borderColor: '#2b6cf6', color: '#2b6cf6' },
            }}
          >
            More tools
            <ExpandMoreIcon sx={{ fontSize: 18, transform: moreOpen ? 'rotate(180deg)' : 'none' }} />
          </ButtonBase>
        </Box>
      )}
      
      <Divider />
      
      <Box sx={{ p: 2 }}>
        {user?.role === 'admin' && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearData}
            sx={{ mb: 1, borderRadius: 2 }}
          >
            Clear Data
          </Button>
        )}
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{ borderRadius: 2 }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* White bar with a hairline under it, like the Sales Hub: the page you are on
          at the left, who is signed in and the two actions at the right. */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#fff',
          color: BAR_TEXT,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root': { fontFamily: TASKS_FONT },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {/* On a phone the menu (and its logo) is tucked away, so the logo sits here. */}
          <ButtonBase onClick={() => navigate('/admin')} aria-label="Go to the Dashboard" title="Dashboard" sx={{ borderRadius: 1, mr: 1.5, display: { xs: 'inline-flex', sm: 'none' } }}>
            <Box component="img" src="/lima-logo-dark.webp" alt="LIMA Automotive Industries" sx={{ height: 28, width: 'auto', display: 'block' }} />
          </ButtonBase>
          <Typography noWrap component="div" sx={{ flexGrow: 1, fontSize: 16, fontWeight: 500 }}>
            {pageTitle}
          </Typography>
          {user && (
            <>
              {/* One button for the person signed in: who they are, and a small menu
                  with the things they can do. On a phone it shrinks to the initial. */}
              <ButtonBase
                onClick={(e) => setUserMenuAt(e.currentTarget)}
                aria-label="Account menu"
                aria-haspopup="menu"
                sx={{
                  gap: 1.25, height: 44, pl: 0.75, pr: { xs: 0.75, sm: 1 }, borderRadius: '12px',
                  border: 1, borderColor: userMenuAt ? 'primary.main' : 'divider',
                  bgcolor: userMenuAt ? BAR_HOVER : '#fff', transition: 'background-color .12s, border-color .12s',
                  '&:hover': { bgcolor: BAR_HOVER, borderColor: 'primary.main' },
                }}
              >
                <Avatar sx={{ width: 30, height: 30, fontSize: 14, fontWeight: 500, fontFamily: TASKS_FONT, bgcolor: headerBgColor }}>
                  {userInitial}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', minWidth: 0, maxWidth: 180 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.25 }}>{userName}</Typography>
                  <Typography noWrap sx={{ fontSize: 12, color: BAR_MUTED, lineHeight: 1.25 }}>{roleText}</Typography>
                </Box>
                <ExpandMoreIcon sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 20, color: BAR_MUTED, transition: 'transform .15s', transform: userMenuAt ? 'rotate(180deg)' : 'none' }} />
              </ButtonBase>
              <MuiMenu
                anchorEl={userMenuAt}
                open={Boolean(userMenuAt)}
                onClose={() => setUserMenuAt(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: {
                  mt: 1, minWidth: 240, borderRadius: '12px', border: 1, borderColor: 'divider',
                  boxShadow: '0 12px 30px rgba(15,30,69,.14)', color: BAR_TEXT,
                  '& .MuiTypography-root, & .MuiMenuItem-root': { fontFamily: TASKS_FONT },
                  '& .MuiMenuItem-root': { fontSize: 14, borderRadius: '8px', mx: 0.75, px: 1.25, minHeight: 38 },
                  '& .MuiListItemIcon-root': { minWidth: 32, color: 'inherit' },
                } } }}
              >
                <Box sx={{ px: 2, pt: 1, pb: 1.25 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 500 }}>{userName}</Typography>
                  <Typography noWrap sx={{ fontSize: 12, color: BAR_MUTED }}>
                    {user.uname && user.uname !== userName ? `${user.uname} · ${roleText}` : roleText}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 0.75 }} />
                {user.role === 'admin' && (
                  <MuiMenuItem onClick={() => { setUserMenuAt(null); handleClearData(); }}>
                    <ListItemIcon><ClearIcon fontSize="small" /></ListItemIcon>
                    Clear browser data
                  </MuiMenuItem>
                )}
                <MuiMenuItem onClick={() => { setUserMenuAt(null); handleLogout(); }} sx={{ color: 'error.main' }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Log out
                </MuiMenuItem>
              </MuiMenu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Tighter gutters on a phone, and the content is never allowed to push the
          // page wider than the screen (which used to force the whole admin to zoom out).
          p: { xs: 1.5, sm: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          bgcolor: isWorker ? '#fafafa' : 'background.default',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout; 